import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { DeactivateDialog } from '@/components/ui/ConfirmDialog'
import { supabase } from '@/integrations/supabase/client'
import { Plus, Edit2, Save, X, Trash2, RotateCcw, Users } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { ImportBeneficiaire } from '@/components/beneficiaires/ImportBeneficiaire'

interface Profile {
    id: string
    email: string
    is_admin: boolean | null
    worker_id: string | null
    created_at: string
    deleted_dt: string | null
}

interface Worker {
    id: string
    name: string | null
    is_active: boolean | null
    is_referent: boolean | null
    created_at: string
}

interface Beneficiary {
    id: string
    name: string | null
    is_active: boolean | null
    created_at: string
}

interface ManageTeamProps {
    onNavigateHome: () => void
}

type TabType = 'users' | 'workers' | 'beneficiaries' | 'import'

export const ManageTeam = ({ onNavigateHome }: ManageTeamProps) => {
    const [activeTab, setActiveTab] = useState<TabType>('users')

    // Profiles data
    const [profiles, setProfiles] = useState<Profile[]>([])
    const [workers, setWorkers] = useState<Worker[]>([])
    const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')

    // Form states
    const [showAddForm, setShowAddForm] = useState(false)
    const [editingProfile, setEditingProfile] = useState<string | null>(null)
    const [editingWorker, setEditingWorker] = useState<string | null>(null)
    const [editingBeneficiary, setEditingBeneficiary] = useState<string | null>(null)

    // Dialog states
    const [showDeactivateDialog, setShowDeactivateDialog] = useState(false)
    const [deactivateData, setDeactivateData] = useState<{
        id: string
        type: 'worker' | 'beneficiary'
        message: string
        itemType: 'travailleur' | 'bénéficiaire'
    } | null>(null)
    const [finDeSuiviForm, setFinDeSuiviForm] = useState({ date: '', raison: '' })

    // Profile form data
    const [profileFormData, setProfileFormData] = useState({
        email: '',
        worker_id: '',
        is_admin: false
    })

    // Worker form data
    const [workerFormData, setWorkerFormData] = useState({
        name: '',
        is_active: true,
        is_referent: false,
    })

    // Beneficiary form data
    const [beneficiaryFormData, setBeneficiaryFormData] = useState({
        name: '',
        prenom: '',
        nom: '',
        is_active: true
    })

    // Fetch data on component mount and tab change
    useEffect(() => {
        fetchData()
    }, [activeTab])

    const fetchData = async () => {
        try {
            setLoading(true)

            if (activeTab === 'users') {
                // Fetch profiles and workers for dropdown
                const { data: profilesData, error: profilesError } = await supabase
                    .from('profiles')
                    .select('*')
                    .order('created_at', { ascending: false })

                if (profilesError) {
                    console.error('Error fetching profiles:', profilesError)
                    setError('Erreur lors du chargement des profils')
                    return
                }

                const { data: workersData, error: workersError } = await supabase
                    .from('workers')
                    .select('*')
                    .eq('is_active', true)
                    .order('name')

                if (workersError) {
                    console.error('Error fetching workers:', workersError)
                    setError('Erreur lors du chargement des travailleurs')
                    return
                }

                setProfiles(profilesData || [])
                setWorkers(workersData || [])

            } else if (activeTab === 'workers') {
                // Fetch all workers
                const { data: workersData, error: workersError } = await supabase
                    .from('workers')
                    .select('*')
                    .order('created_at', { ascending: false })

                if (workersError) {
                    console.error('Error fetching workers:', workersError)
                    setError('Erreur lors du chargement des travailleurs')
                    return
                }

                setWorkers(workersData || [])

            } else if (activeTab === 'beneficiaries') {
                // Fetch all beneficiaries
                const { data: beneficiariesData, error: beneficiariesError } = await supabase
                    .from('beneficiaries')
                    .select('*')
                    .order('created_at', { ascending: false })

                if (beneficiariesError) {
                    console.error('Error fetching beneficiaries:', beneficiariesError)
                    setError('Erreur lors du chargement des locataires')
                    return
                }

                setBeneficiaries(beneficiariesData || [])
            }
        } catch (err) {
            console.error('Error fetching data:', err)
            setError('Erreur lors du chargement des données')
        } finally {
            setLoading(false)
        }
    }

    const resetForms = () => {
        setShowAddForm(false)
        setEditingProfile(null)
        setEditingWorker(null)
        setEditingBeneficiary(null)
        setProfileFormData({ email: '', worker_id: '', is_admin: false })
        setWorkerFormData({ name: '', is_active: true, is_referent: false })
        setBeneficiaryFormData({ name: '', prenom: '', nom: '', is_active: true })
    }

    const handleTabChange = (tab: TabType) => {
        setActiveTab(tab)
        resetForms()
        setError('')
        setSuccess('')
    }

    // Profile handlers
    const handleAddProfile = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!profileFormData.email) {
            setError('L\'email est requis')
            return
        }

        try {
            setError('')
            setSuccess('')

            const insertData = {
                email: profileFormData.email,
                worker_id: profileFormData.worker_id || null,
                is_admin: profileFormData.is_admin
            }

            const { data, error } = await supabase
                .from('profiles')
                .insert([insertData])
                .select()
                .single()

            if (error) {
                console.error('Error adding profile:', error)
                setError('Erreur lors de l\'ajout du profil')
                return
            }

            setSuccess('Profil ajouté avec succès')
            setProfileFormData({ email: '', worker_id: '', is_admin: false })
            setShowAddForm(false)
            fetchData()
        } catch (err) {
            console.error('Error adding profile:', err)
            setError('Erreur lors de l\'ajout du profil')
        }
    }

    const handleEditProfile = async (profileId: string) => {
        try {
            setError('')
            setSuccess('')

            const updateData = {
                worker_id: profileFormData.worker_id || null,
                is_admin: profileFormData.is_admin
            }

            const { error } = await supabase
                .from('profiles')
                .update(updateData)
                .eq('id', profileId)

            if (error) {
                console.error('Error updating profile:', error)
                setError('Erreur lors de la modification du profil')
                return
            }

            setSuccess('Profil modifié avec succès')
            setEditingProfile(null)
            setProfileFormData({ email: '', worker_id: '', is_admin: false })
            fetchData()
        } catch (err) {
            console.error('Error updating profile:', err)
            setError('Erreur lors de la modification du profil')
        }
    }

    const handleDeleteProfile = async (profileId: string) => {
        try {
            setError('')
            setSuccess('')

            const { error } = await supabase
                .from('profiles')
                .update({ deleted_dt: new Date().toISOString() })
                .eq('id', profileId)

            if (error) {
                console.error('Error deleting profile:', error)
                setError('Erreur lors de la suppression du profil')
                return
            }

            setSuccess('Profil supprimé avec succès')
            fetchData()
        } catch (err) {
            console.error('Error deleting profile:', err)
            setError('Erreur lors de la suppression du profil')
        }
    }

    const handleRestoreProfile = async (profileId: string) => {
        try {
            setError('')
            setSuccess('')

            const { error } = await supabase
                .from('profiles')
                .update({ deleted_dt: null })
                .eq('id', profileId)

            if (error) {
                console.error('Error restoring profile:', error)
                setError('Erreur lors de la restauration du profil')
                return
            }

            setSuccess('Profil restauré avec succès')
            fetchData()
        } catch (err) {
            console.error('Error restoring profile:', err)
            setError('Erreur lors de la restauration du profil')
        }
    }

    // Worker handlers
    const handleAddWorker = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!workerFormData.name) {
            setError('Le nom est requis')
            return
        }

        try {
            setError('')
            setSuccess('')

            const { data, error } = await supabase
                .from('workers')
                .insert([workerFormData])
                .select()
                .single()

            if (error) {
                console.error('Error adding worker:', error)
                setError('Erreur lors de l\'ajout du travailleur')
                return
            }

            setSuccess('Travailleur ajouté avec succès')
            setWorkerFormData({ name: '', is_active: true, is_referent: false })
            setShowAddForm(false)
            fetchData()
        } catch (err) {
            console.error('Error adding worker:', err)
            setError('Erreur lors de l\'ajout du travailleur')
        }
    }

    const handleEditWorker = async (workerId: string) => {
        try {
            setError('')
            setSuccess('')

            const { error } = await supabase
                .from('workers')
                .update(workerFormData)
                .eq('id', workerId)

            if (error) {
                console.error('Error updating worker:', error)
                setError('Erreur lors de la modification du travailleur')
                return
            }

            setSuccess('Travailleur modifié avec succès')
            setEditingWorker(null)
            setWorkerFormData({ name: '', is_active: true, is_referent: false })
            fetchData()
        } catch (err) {
            console.error('Error updating worker:', err)
            setError('Erreur lors de la modification du travailleur')
        }
    }

    const checkWorkerReferences = async (workerId: string) => {
        try {
            // Check in profiles
            const { data: profileRefs } = await supabase
                .from('profiles')
                .select('id')
                .eq('worker_id', workerId)
                .limit(1)

            if (profileRefs && profileRefs.length > 0) {
                return 'Impossible de supprimer ce travailleur car il est associé à des profils utilisateur. Vous pouvez le désactiver en cliquant sur "Modifier" puis en décochant "Actif". Il restera disponible pour l\'historique et les statistiques mais ne sera plus sélectionnable dans les nouveaux échanges.'
            }

            // Check in exchanges
            const { data: exchangeRefs } = await supabase
                .from('exchanges')
                .select('id')
                .eq('worker_id', workerId)
                .limit(1)

            if (exchangeRefs && exchangeRefs.length > 0) {
                return 'Impossible de supprimer ce travailleur car il est référencé dans des échanges. Vous pouvez le désactiver en cliquant sur "Modifier" puis en décochant "Actif". Il restera disponible pour l\'historique et les statistiques mais ne sera plus sélectionnable dans les nouveaux échanges.'
            }

            // Check in notes
            const { data: noteRefs } = await supabase
                .from('notes')
                .select('id')
                .eq('worker_id', workerId)
                .limit(1)

            if (noteRefs && noteRefs.length > 0) {
                return 'Impossible de supprimer ce travailleur car il est référencé dans des notes. Vous pouvez le désactiver en cliquant sur "Modifier" puis en décochant "Actif". Il restera disponible pour l\'historique et les statistiques mais ne sera plus sélectionnable dans les nouveaux échanges.'
            }

            // Check in stats
            const { data: statRefs } = await supabase
                .from('stats')
                .select('id')
                .eq('worker_id', workerId)
                .limit(1)

            if (statRefs && statRefs.length > 0) {
                return 'Impossible de supprimer ce travailleur car il est référencé dans des statistiques. Vous pouvez le désactiver en cliquant sur "Modifier" puis en décochant "Actif". Il restera disponible pour l\'historique et les statistiques mais ne sera plus sélectionnable dans les nouveaux échanges.'
            }

            // Check in colleague_ids arrays
            const { data: exchangeColleagueRefs } = await supabase
                .from('exchanges')
                .select('id')
                .contains('colleague_ids', [workerId])
                .limit(1)

            if (exchangeColleagueRefs && exchangeColleagueRefs.length > 0) {
                return 'Impossible de supprimer ce travailleur car il est référencé comme collègue dans des échanges. Vous pouvez le désactiver en cliquant sur "Modifier" puis en décochant "Actif". Il restera disponible pour l\'historique et les statistiques mais ne sera plus sélectionnable dans les nouveaux échanges.'
            }

            const { data: noteColleagueRefs } = await supabase
                .from('notes')
                .select('id')
                .contains('colleague_ids', [workerId])
                .limit(1)

            if (noteColleagueRefs && noteColleagueRefs.length > 0) {
                return 'Impossible de supprimer ce travailleur car il est référencé comme collègue dans des notes. Vous pouvez le désactiver en cliquant sur "Modifier" puis en décochant "Actif". Il restera disponible pour l\'historique et les statistiques mais ne sera plus sélectionnable dans les nouveaux échanges.'
            }

            const { data: statColleagueRefs } = await supabase
                .from('stats')
                .select('id')
                .contains('colleague_ids', [workerId])
                .limit(1)

            if (statColleagueRefs && statColleagueRefs.length > 0) {
                return 'Impossible de supprimer ce travailleur car il est référencé comme collègue dans des statistiques. Vous pouvez le désactiver en cliquant sur "Modifier" puis en décochant "Actif". Il restera disponible pour l\'historique et les statistiques mais ne sera plus sélectionnable dans les nouveaux échanges.'
            }

            return null
        } catch (err) {
            console.error('Error checking references:', err)
            return 'Erreur lors de la vérification des références'
        }
    }

    const handleDeleteWorker = async (workerId: string) => {
        try {
            setError('')
            setSuccess('')

            const refError = await checkWorkerReferences(workerId)
            if (refError) {
                setDeactivateData({
                    id: workerId,
                    type: 'worker',
                    message: refError,
                    itemType: 'travailleur'
                })
                setShowDeactivateDialog(true)
                return
            }

            const { error } = await supabase
                .from('workers')
                .delete()
                .eq('id', workerId)

            if (error) {
                console.error('Error deleting worker:', error)
                setError('Erreur lors de la suppression du travailleur')
                return
            }

            setSuccess('Travailleur supprimé avec succès')
            fetchData()
        } catch (err) {
            console.error('Error deleting worker:', err)
            setError('Erreur lors de la suppression du travailleur')
        }
    }

    // Beneficiary handlers
    const handleAddBeneficiary = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!beneficiaryFormData.name) {
            setError('Le nom est requis')
            return
        }

        try {
            setError('')
            setSuccess('')

            const { data, error } = await supabase
                .from('beneficiaries')
                .insert([beneficiaryFormData])
                .select()
                .single()

            if (error) {
                console.error('Error adding beneficiary:', error)
                setError('Erreur lors de l\'ajout du bénéficiaire')
                return
            }

            setSuccess('Locataire ajouté avec succès')
            setBeneficiaryFormData({ name: '', prenom: '', nom: '', is_active: true })
            setShowAddForm(false)
            fetchData()
        } catch (err) {
            console.error('Error adding beneficiary:', err)
            setError('Erreur lors de l\'ajout du bénéficiaire')
        }
    }

    const handleEditBeneficiary = async (beneficiaryId: string) => {
        try {
            setError('')
            setSuccess('')

            const { error } = await supabase
                .from('beneficiaries')
                .update(beneficiaryFormData)
                .eq('id', beneficiaryId)

            if (error) {
                console.error('Error updating beneficiary:', error)
                setError('Erreur lors de la modification du locataire')
                return
            }

            setSuccess('Locataire modifié avec succès')
            setEditingBeneficiary(null)
            setBeneficiaryFormData({ name: '', prenom: '', nom: '', is_active: true })
            fetchData()
        } catch (err) {
            console.error('Error updating beneficiary:', err)
            setError('Erreur lors de la modification du locataire')
        }
    }

    const checkBeneficiaryReferences = async (beneficiaryId: string) => {
        try {
            // Check in exchanges
            const { data: exchangeRefs } = await supabase
                .from('exchanges')
                .select('id')
                .eq('beneficiary_id', beneficiaryId)
                .limit(1)

            if (exchangeRefs && exchangeRefs.length > 0) {
                return 'Impossible de supprimer ce locataire car il est référencé dans des échanges. Vous pouvez le désactiver en cliquant sur "Modifier" puis en décochant "Actif". Il restera disponible pour l\'historique et les statistiques mais ne sera plus sélectionnable dans les nouveaux échanges.'
            }

            // Check in notes
            const { data: noteRefs } = await supabase
                .from('notes')
                .select('id')
                .eq('beneficiary_id', beneficiaryId)
                .limit(1)

            if (noteRefs && noteRefs.length > 0) {
                return 'Impossible de supprimer ce locataire car il est référencé dans des notes. Vous pouvez le désactiver en cliquant sur "Modifier" puis en décochant "Actif". Il restera disponible pour l\'historique et les statistiques mais ne sera plus sélectionnable dans les nouveaux échanges.'
            }

            // Check in stats
            const { data: statRefs } = await supabase
                .from('stats')
                .select('id')
                .eq('beneficiary_id', beneficiaryId)
                .limit(1)

            if (statRefs && statRefs.length > 0) {
                return 'Impossible de supprimer ce locataire car il est référencé dans des statistiques. Vous pouvez le désactiver en cliquant sur "Modifier" puis en décochant "Actif". Il restera disponible pour l\'historique et les statistiques mais ne sera plus sélectionnable dans les nouveaux échanges.'
            }

            // Check in colleague_ids arrays
            const { data: exchangeColleagueRefs } = await supabase
                .from('exchanges')
                .select('id')
                .contains('colleague_ids', [beneficiaryId])
                .limit(1)

            if (exchangeColleagueRefs && exchangeColleagueRefs.length > 0) {
                return 'Impossible de supprimer ce locataire car il est référencé comme collègue dans des échanges. Vous pouvez le désactiver en cliquant sur "Modifier" puis en décochant "Actif". Il restera disponible pour l\'historique et les statistiques mais ne sera plus sélectionnable dans les nouveaux échanges.'
            }

            const { data: noteColleagueRefs } = await supabase
                .from('notes')
                .select('id')
                .contains('colleague_ids', [beneficiaryId])
                .limit(1)

            if (noteColleagueRefs && noteColleagueRefs.length > 0) {
                return 'Impossible de supprimer ce locataire car il est référencé comme collègue dans des notes. Vous pouvez le désactiver en cliquant sur "Modifier" puis en décochant "Actif". Il restera disponible pour l\'historique et les statistiques mais ne sera plus sélectionnable dans les nouveaux échanges.'
            }

            const { data: statColleagueRefs } = await supabase
                .from('stats')
                .select('id')
                .contains('colleague_ids', [beneficiaryId])
                .limit(1)

            if (statColleagueRefs && statColleagueRefs.length > 0) {
                return 'Impossible de supprimer ce locataire car il est référencé comme collègue dans des statistiques. Vous pouvez le désactiver en cliquant sur "Modifier" puis en décochant "Actif". Il restera disponible pour l\'historique et les statistiques mais ne sera plus sélectionnable dans les nouveaux échanges.'
            }

            return null
        } catch (err) {
            console.error('Error checking references:', err)
            return 'Erreur lors de la vérification des références'
        }
    }

    const handleDeleteBeneficiary = async (beneficiaryId: string) => {
        try {
            setError('')
            setSuccess('')

            const refError = await checkBeneficiaryReferences(beneficiaryId)
            if (refError) {
                setDeactivateData({
                    id: beneficiaryId,
                    type: 'beneficiary',
                    message: refError,
                    itemType: 'bénéficiaire'
                })
                setShowDeactivateDialog(true)
                return
            }

            const { error } = await supabase
                .from('beneficiaries')
                .delete()
                .eq('id', beneficiaryId)

            if (error) {
                console.error('Error deleting beneficiary:', error)
                setError('Erreur lors de la suppression du locataire')
                return
            }

            setSuccess('Locataire supprimé avec succès')
            fetchData()
        } catch (err) {
            console.error('Error deleting beneficiary:', err)
            setError('Erreur lors de la suppression du locataire')
        }
    }

    // Helper functions
    const startEditProfile = (profile: Profile) => {
        setEditingProfile(profile.id)
        setProfileFormData({
            email: profile.email,
            worker_id: profile.worker_id || '',
            is_admin: profile.is_admin || false
        })
    }

    const startEditWorker = (worker: Worker) => {
        setEditingWorker(worker.id)
        setWorkerFormData({
            name: worker.name || '',
            is_active: worker.is_active !== false,
            is_referent: worker.is_referent === true,
        })
    }

    const startEditBeneficiary = (beneficiary: Beneficiary) => {
        setEditingBeneficiary(beneficiary.id)
        setBeneficiaryFormData({
            name: beneficiary.name || '',
            is_active: beneficiary.is_active !== false
        })
    }

    const getWorkerName = (workerId: string | null) => {
        if (!workerId) return 'Aucun'
        const worker = workers.find(w => w.id === workerId)
        return worker?.name || 'Inconnu'
    }

    const handleDeactivateConfirm = async () => {
        if (!deactivateData) return

        try {
            setError('')
            setSuccess('')

            if (deactivateData.type === 'worker') {
                const { error } = await supabase
                    .from('workers')
                    .update({ is_active: false })
                    .eq('id', deactivateData.id)

                if (error) {
                    console.error('Error deactivating worker:', error)
                    setError('Erreur lors de la désactivation du travailleur')
                    return
                }

                setSuccess('Travailleur désactivé avec succès')
            } else {
                const updateData: Record<string, unknown> = { is_active: false }
                if (finDeSuiviForm.date) updateData.date_suspension_termination = finDeSuiviForm.date
                if (finDeSuiviForm.raison) updateData.cause_suspension_termination = finDeSuiviForm.raison

                const { error } = await supabase
                    .from('beneficiaries')
                    .update(updateData)
                    .eq('id', deactivateData.id)

                if (error) {
                    console.error('Error deactivating beneficiary:', error)
                    setError('Erreur lors de la désactivation du locataire')
                    return
                }

                setSuccess('Locataire désactivé avec succès')
            }

            setShowDeactivateDialog(false)
            setDeactivateData(null)
            setFinDeSuiviForm({ date: '', raison: '' })
            fetchData()
        } catch (err) {
            console.error('Error deactivating:', err)
            setError('Erreur lors de la désactivation')
        }
    }

    const handleDeactivateCancel = () => {
        setShowDeactivateDialog(false)
        setDeactivateData(null)
        setFinDeSuiviForm({ date: '', raison: '' })
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Chargement des données...</p>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <PageHeader title="Gestion d'équipe" icon={<Users size={20} />} onBack={onNavigateHome} />

            {/* Tab Navigation */}
            <div className="bg-white shadow">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex space-x-8">
                        <button
                            onClick={() => handleTabChange('users')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'users'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            Utilisateurs
                        </button>
                        <button
                            onClick={() => handleTabChange('workers')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'workers'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            Travailleurs
                        </button>
                        <button
                            onClick={() => handleTabChange('beneficiaries')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'beneficiaries'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            Bénéficiaires
                        </button>
                        <button
                            onClick={() => handleTabChange('import')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'import'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            Importer fiche bénéficiaire
                        </button>
                    </div>
                </div>
            </div>

            <main className="max-w-6xl mx-auto py-8 px-4">
                {/* Messages */}
                {error && (
                    <div className="mb-4 rounded-md bg-red-50 p-4">
                        <div className="text-sm text-red-700">{error}</div>
                    </div>
                )}

                {success && (
                    <div className="mb-4 rounded-md bg-green-50 p-4">
                        <div className="text-sm text-green-700">{success}</div>
                    </div>
                )}

                {/* Add Button */}
                {activeTab !== 'import' && <div className="mb-6">
                    <Button
                        onClick={() => setShowAddForm(!showAddForm)}
                        className="flex items-center space-x-2"
                    >
                        <Plus className="h-4 w-4" />
                        <span>
                            {activeTab === 'users' && 'Ajouter un profil'}
                            {activeTab === 'workers' && 'Ajouter un travailleur'}
                            {activeTab === 'beneficiaries' && 'Ajouter un locataire'}
                        </span>
                    </Button>
                </div>}

                {/* Add Forms */}
                {showAddForm && activeTab === 'users' && (
                    <div className="mb-8 bg-white rounded-lg shadow p-6">
                        <h2 className="text-lg font-semibold mb-4">Nouveau profil</h2>
                        <form onSubmit={handleAddProfile} className="space-y-4">
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                    Email
                                </label>
                                <input
                                    id="email"
                                    type="email"
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                                    value={profileFormData.email}
                                    onChange={(e) => setProfileFormData(prev => ({ ...prev, email: e.target.value }))}
                                    placeholder="email@exemple.com"
                                />
                            </div>

                            <div>
                                <label htmlFor="worker_id" className="block text-sm font-medium text-gray-700 mb-1">
                                    Travailleur associé
                                </label>
                                <select
                                    id="worker_id"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                                    value={profileFormData.worker_id}
                                    onChange={(e) => setProfileFormData(prev => ({ ...prev, worker_id: e.target.value }))}
                                >
                                    <option value="">Aucun travailleur</option>
                                    {workers.map(worker => (
                                        <option key={worker.id} value={worker.id}>
                                            {worker.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex items-center">
                                <input
                                    id="is_admin"
                                    type="checkbox"
                                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                    checked={profileFormData.is_admin}
                                    onChange={(e) => setProfileFormData(prev => ({ ...prev, is_admin: e.target.checked }))}
                                />
                                <label htmlFor="is_admin" className="ml-2 block text-sm text-gray-700">
                                    Administrateur
                                </label>
                            </div>

                            <div className="flex space-x-2">
                                <Button type="submit">Ajouter</Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        setShowAddForm(false)
                                        setProfileFormData({ email: '', worker_id: '', is_admin: false })
                                    }}
                                >
                                    Annuler
                                </Button>
                            </div>
                        </form>
                    </div>
                )}

                {showAddForm && activeTab === 'workers' && (
                    <div className="mb-8 bg-white rounded-lg shadow p-6">
                        <h2 className="text-lg font-semibold mb-4">Nouveau travailleur</h2>
                        <form onSubmit={handleAddWorker} className="space-y-4">
                            <div>
                                <label htmlFor="worker_name" className="block text-sm font-medium text-gray-700 mb-1">
                                    Nom
                                </label>
                                <input
                                    id="worker_name"
                                    type="text"
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                                    value={workerFormData.name}
                                    onChange={(e) => setWorkerFormData(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="Nom du travailleur"
                                />
                            </div>

                            <div className="flex items-center gap-6">
                                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                    <input
                                        id="worker_is_active"
                                        type="checkbox"
                                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                        checked={workerFormData.is_active}
                                        onChange={(e) => setWorkerFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                                    />
                                    Actif
                                </label>
                                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                    <input
                                        id="worker_is_referent"
                                        type="checkbox"
                                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                        checked={workerFormData.is_referent}
                                        onChange={(e) => setWorkerFormData(prev => ({ ...prev, is_referent: e.target.checked }))}
                                    />
                                    Référent.e
                                </label>
                            </div>

                            <div className="flex space-x-2">
                                <Button type="submit">Ajouter</Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        setShowAddForm(false)
                                        setWorkerFormData({ name: '', is_active: true, is_referent: false })
                                    }}
                                >
                                    Annuler
                                </Button>
                            </div>
                        </form>
                    </div>
                )}

                {showAddForm && activeTab === 'beneficiaries' && (
                    <div className="mb-8 bg-white rounded-lg shadow p-6">
                        <h2 className="text-lg font-semibold mb-4">Nouveau locataire</h2>
                        <form onSubmit={handleAddBeneficiary} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="beneficiary_prenom" className="block text-sm font-medium text-gray-700 mb-1">
                                        Prénom
                                    </label>
                                    <input
                                        id="beneficiary_prenom"
                                        type="text"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                                        value={beneficiaryFormData.prenom}
                                        onChange={(e) => setBeneficiaryFormData(prev => ({ ...prev, prenom: e.target.value }))}
                                        placeholder="Prénom"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="beneficiary_nom" className="block text-sm font-medium text-gray-700 mb-1">
                                        Nom
                                    </label>
                                    <input
                                        id="beneficiary_nom"
                                        type="text"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                                        value={beneficiaryFormData.nom}
                                        onChange={(e) => setBeneficiaryFormData(prev => ({ ...prev, nom: e.target.value }))}
                                        placeholder="Nom de famille"
                                    />
                                </div>
                            </div>
                            <div>
                                <label htmlFor="beneficiary_name" className="block text-sm font-medium text-gray-700 mb-1">
                                    Alias
                                </label>
                                <input
                                    id="beneficiary_name"
                                    type="text"
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                                    value={beneficiaryFormData.name}
                                    onChange={(e) => setBeneficiaryFormData(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="Alias (obligatoire)"
                                />
                            </div>

                            <div className="flex items-center">
                                <input
                                    id="beneficiary_is_active"
                                    type="checkbox"
                                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                    checked={beneficiaryFormData.is_active}
                                    onChange={(e) => setBeneficiaryFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                                />
                                <label htmlFor="beneficiary_is_active" className="ml-2 block text-sm text-gray-700">
                                    Actif
                                </label>
                            </div>

                            <div className="flex space-x-2">
                                <Button type="submit">Ajouter</Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        setShowAddForm(false)
                                        setBeneficiaryFormData({ name: '', prenom: '', nom: '', is_active: true })
                                    }}
                                >
                                    Annuler
                                </Button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Users Tab Content */}
                {activeTab === 'users' && (
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold">Profils existants</h2>
                        </div>

                        {profiles.length === 0 ? (
                            <div className="px-6 py-8 text-center text-gray-500">
                                Aucun profil trouvé
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-200">
                                {profiles.map(profile => (
                                    <div key={profile.id} className={`px-6 py-4 ${profile.deleted_dt ? 'bg-red-50 opacity-75' : ''}`}>
                                        {editingProfile === profile.id ? (
                                            // Edit form
                                            <div className="space-y-4">
                                                <div className="text-sm text-gray-900 font-medium">
                                                    {profile.email}
                                                    {profile.deleted_dt && (
                                                        <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-800 rounded">
                                                            SUPPRIMÉ
                                                        </span>
                                                    )}
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Travailleur associé
                                                    </label>
                                                    <select
                                                        className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                                                        value={profileFormData.worker_id}
                                                        onChange={(e) => setProfileFormData(prev => ({ ...prev, worker_id: e.target.value }))}
                                                        disabled={!!profile.deleted_dt}
                                                    >
                                                        <option value="">Aucun travailleur</option>
                                                        {workers.map(worker => (
                                                            <option key={worker.id} value={worker.id}>
                                                                {worker.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div className="flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                                        checked={profileFormData.is_admin}
                                                        onChange={(e) => setProfileFormData(prev => ({ ...prev, is_admin: e.target.checked }))}
                                                        disabled={!!profile.deleted_dt}
                                                    />
                                                    <label className="ml-2 block text-sm text-gray-700">
                                                        Administrateur
                                                    </label>
                                                </div>

                                                <div className="flex space-x-2">
                                                    {!profile.deleted_dt && (
                                                        <Button
                                                            size="sm"
                                                            onClick={() => handleEditProfile(profile.id)}
                                                        >
                                                            <Save className="h-4 w-4 mr-1" />
                                                            Sauvegarder
                                                        </Button>
                                                    )}
                                                    <Button size="sm" variant="outline" onClick={() => {
                                                        setEditingProfile(null)
                                                        setProfileFormData({ email: '', worker_id: '', is_admin: false })
                                                    }}>
                                                        <X className="h-4 w-4 mr-1" />
                                                        Annuler
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            // Display mode
                                            <div className="flex justify-between items-center">
                                                <div className="space-y-1">
                                                    <div className="text-sm font-medium text-gray-900 flex items-center">
                                                        {profile.email}
                                                        {profile.deleted_dt && (
                                                            <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-800 rounded">
                                                                SUPPRIMÉ
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        Travailleur: {getWorkerName(profile.worker_id)}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        Statut: {profile.is_admin ? 'Administrateur' : 'Utilisateur'}
                                                    </div>
                                                    <div className="text-xs text-gray-400">
                                                        Créé le: {new Date(profile.created_at).toLocaleDateString('fr-FR')}
                                                    </div>
                                                    {profile.deleted_dt && (
                                                        <div className="text-xs text-red-500">
                                                            Supprimé le: {new Date(profile.deleted_dt).toLocaleDateString('fr-FR')}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="flex space-x-2">
                                                    {!profile.deleted_dt ? (
                                                        <>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => startEditProfile(profile)}
                                                            >
                                                                <Edit2 className="h-4 w-4 mr-1" />
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => handleDeleteProfile(profile.id)}
                                                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                            >
                                                                <Trash2 className="h-4 w-4 mr-1" />
                                                                Suspendre
                                                            </Button>
                                                        </>
                                                    ) : (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => handleRestoreProfile(profile.id)}
                                                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                                        >
                                                            <RotateCcw className="h-4 w-4 mr-1" />
                                                            Restaurer
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Workers Tab Content */}
                {activeTab === 'workers' && (
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold">Travailleurs</h2>
                        </div>

                        {workers.length === 0 ? (
                            <div className="px-6 py-8 text-center text-gray-500">
                                Aucun travailleur trouvé
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-200">
                                {workers.map(worker => (
                                    <div key={worker.id} className={`px-6 py-4 ${!worker.is_active ? 'bg-gray-50 opacity-75' : ''}`}>
                                        {editingWorker === worker.id ? (
                                            // Edit form
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Nom
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                                                        value={workerFormData.name}
                                                        onChange={(e) => setWorkerFormData(prev => ({ ...prev, name: e.target.value }))}
                                                        placeholder="Nom du travailleur"
                                                    />
                                                </div>

                                                <div className="flex items-center gap-6">
                                                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                                            checked={workerFormData.is_active}
                                                            onChange={(e) => setWorkerFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                                                        />
                                                        Actif
                                                    </label>
                                                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                                            checked={workerFormData.is_referent}
                                                            onChange={(e) => setWorkerFormData(prev => ({ ...prev, is_referent: e.target.checked }))}
                                                        />
                                                        Référent.e
                                                    </label>
                                                </div>

                                                <div className="flex space-x-2">
                                                    <Button
                                                        size="sm"
                                                        onClick={() => handleEditWorker(worker.id)}
                                                    >
                                                        <Save className="h-4 w-4 mr-1" />
                                                        Sauvegarder
                                                    </Button>
                                                    <Button size="sm" variant="outline" onClick={() => {
                                                        setEditingWorker(null)
                                                        setWorkerFormData({ name: '', is_active: true, is_referent: false })
                                                    }}>
                                                        <X className="h-4 w-4 mr-1" />
                                                        Annuler
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            // Display mode
                                            <div className="flex justify-between items-center">
                                                <div className="space-y-1">
                                                    <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                                                        {worker.name || 'Sans nom'}
                                                        {worker.is_referent && (
                                                            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                                                                Référent.e
                                                            </span>
                                                        )}
                                                        {!worker.is_active && (
                                                            <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-800 rounded">
                                                                INACTIF
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        Statut: {worker.is_active ? 'Actif' : 'Inactif'}
                                                    </div>
                                                    <div className="text-xs text-gray-400">
                                                        Créé le: {new Date(worker.created_at).toLocaleDateString('fr-FR')}
                                                    </div>
                                                </div>

                                                <div className="flex space-x-2">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => startEditWorker(worker)}
                                                    >
                                                        <Edit2 className="h-4 w-4 mr-1" />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleDeleteWorker(worker.id)}
                                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                    >
                                                        <Trash2 className="h-4 w-4 mr-1" />
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Beneficiaries Tab Content */}
                {activeTab === 'import' && (
                    <ImportBeneficiaire onNavigateBack={() => handleTabChange('beneficiaries')} />
                )}

                {activeTab === 'beneficiaries' && (
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold">Locataires</h2>
                        </div>

                        {beneficiaries.length === 0 ? (
                            <div className="px-6 py-8 text-center text-gray-500">
                                Aucun locataire trouvé
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-200">
                                {beneficiaries.map(beneficiary => (
                                    <div key={beneficiary.id} className={`px-6 py-4 ${!beneficiary.is_active ? 'bg-gray-50 opacity-75' : ''}`}>
                                        {editingBeneficiary === beneficiary.id ? (
                                            // Edit form
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Nom
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                                                        value={beneficiaryFormData.name}
                                                        onChange={(e) => setBeneficiaryFormData(prev => ({ ...prev, name: e.target.value }))}
                                                        placeholder="Nom du locataire"
                                                    />
                                                </div>

                                                <div className="flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                                        checked={beneficiaryFormData.is_active}
                                                        onChange={(e) => setBeneficiaryFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                                                    />
                                                    <label className="ml-2 block text-sm text-gray-700">
                                                        Actif
                                                    </label>
                                                </div>

                                                <div className="flex space-x-2">
                                                    <Button
                                                        size="sm"
                                                        onClick={() => handleEditBeneficiary(beneficiary.id)}
                                                    >
                                                        <Save className="h-4 w-4 mr-1" />
                                                        Sauvegarder
                                                    </Button>
                                                    <Button size="sm" variant="outline" onClick={() => {
                                                        setEditingBeneficiary(null)
                                                        setBeneficiaryFormData({ name: '', prenom: '', nom: '', is_active: true })
                                                    }}>
                                                        <X className="h-4 w-4 mr-1" />
                                                        Annuler
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            // Display mode
                                            <div className="flex justify-between items-center">
                                                <div className="space-y-1">
                                                    <div className="text-sm font-medium text-gray-900 flex items-center">
                                                        {[beneficiary.prenom, beneficiary.nom].filter(Boolean).join(' ') || beneficiary.name || 'Locataire'}
                                                        {!beneficiary.is_active && (
                                                            <span className="ml-2 px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">
                                                                INACTIF
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        Statut: {beneficiary.is_active ? 'Actif' : 'Inactif'}
                                                    </div>
                                                    <div className="text-xs text-gray-400">
                                                        Créé le: {new Date(beneficiary.created_at).toLocaleDateString('fr-FR')}
                                                    </div>
                                                </div>

                                                <div className="flex space-x-2">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => startEditBeneficiary(beneficiary)}
                                                    >
                                                        <Edit2 className="h-4 w-4 mr-1" />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleDeleteBeneficiary(beneficiary.id)}
                                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                    >
                                                        <Trash2 className="h-4 w-4 mr-1" />
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </main>

            {/* Deactivate Dialog */}
            {showDeactivateDialog && deactivateData && deactivateData.type === 'worker' && (
                <DeactivateDialog
                    title="Suppression impossible"
                    message={deactivateData.message}
                    itemType={deactivateData.itemType}
                    onDeactivate={handleDeactivateConfirm}
                    onCancel={handleDeactivateCancel}
                />
            )}
            {showDeactivateDialog && deactivateData && deactivateData.type === 'beneficiary' && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-lg mx-4 w-full">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Suppression impossible</h3>
                        <p className="text-gray-600 mb-5 text-sm leading-relaxed">{deactivateData.message}</p>
                        <div className="border-t border-gray-100 pt-4 mb-5">
                            <p className="text-sm font-medium text-gray-700 mb-3">Suspension de suivi (optionnel)</p>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Date de suspension de suivi</label>
                                    <input
                                        type="date"
                                        value={finDeSuiviForm.date}
                                        onChange={e => setFinDeSuiviForm(f => ({ ...f, date: e.target.value }))}
                                        className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Raison</label>
                                    <select
                                        value={finDeSuiviForm.raison}
                                        onChange={e => setFinDeSuiviForm(f => ({ ...f, raison: e.target.value }))}
                                        className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="">— Choisir —</option>
                                        <option>Perte de contact</option>
                                        <option>Demande explicite</option>
                                        <option>Décès</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3">
                            <Button variant="outline" onClick={handleDeactivateCancel}>Annuler</Button>
                            <Button onClick={handleDeactivateConfirm} className="bg-orange-600 hover:bg-orange-700">
                                Désactiver le locataire
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
} 